import React, {useState, useEffect, useRef, useCallback} from 'react'
import io from 'socket.io-client'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import styled from '@emotion/styled'
import {toast} from 'react-toastify'
import {useAuthClient} from '../utils/use-auth-client'
import {ReactComponent as RefreshIcon} from '../assets/refresh-icon.svg'
import {OOE_SERVICE_URL as REACT_APP_OOE_SERVICE_URL} from '../utils/api-base'
import {theme} from '../pages/ProposalTool/features/Dashboard/theme'
import {
  ContentArea,
  MainArea,
  ModalOverlay,
  ModalBox,
  ModalCancelBtn,
  ModalActions,
  FormInput,
  FormLabel,
  FormField,
} from '../pages/ProposalTool/features/Dashboard/styled.components'
import {
  TableWrapper,
  StyledTable,
  THead,
  TBody,
  TR,
  TH,
  TD,
  EmptyState,
  EmptyStateTitle,
} from '../pages/ProposalTool/components/ProposalTable/styled.components'
import {
  DateRangeWrapper,
  DateRangeTrigger,
  CalendarPopout,
} from '../pages/ProposalTool/components/ProposalFilters/styled.components'
import {ProposalPagination} from '../pages/ProposalTool/components/ProposalPagination'
import {PTCalender, PTCaretDown} from '../assets'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

const DEFAULT_TIMEZONE = 'America/Chicago'

const FiltersRow = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 12px;
`

const ClearBtn = styled(ModalCancelBtn)`
  white-space: nowrap;
  background: #f4f6fb;
  border-color: #0327a9;
  color: #0327a9;

  &:hover {
    background: #e8ecf9;
  }
`

const formatDate = dateStr =>
  dayjs(dateStr).tz(DEFAULT_TIMEZONE).format('MM/DD/YYYY hh:mmA z')

const CountdownTimer = ({expiryDate}) => {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    if (!expiryDate) return

    const calculate = () => {
      const diff = dayjs(expiryDate).diff(dayjs())
      if (diff <= 0) {
        setTimeLeft('Expired')
        return
      }
      const d = Math.floor(diff / 86400000)
      const h = Math.floor((diff % 86400000) / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      setTimeLeft(
        `${String(d).padStart(2, '0')}d : ${String(h).padStart(
          2,
          '0',
        )}h : ${String(m).padStart(2, '0')}m : ${String(s).padStart(2, '0')}s`,
      )
    }

    calculate()
    const timer = setInterval(calculate, 1000)
    return () => clearInterval(timer)
  }, [expiryDate])

  if (!expiryDate) return 'N/A'

  const isExpired = dayjs(expiryDate).isBefore(dayjs())

  return (
    <p
      style={{
        fontFamily: 'monospace',
        fontWeight: 'bold',
        margin: 0,
        whiteSpace: 'nowrap',
        color: isExpired ? '#cc0000' : 'inherit',
      }}
    >
      {timeLeft}
    </p>
  )
}

const useDebouncedValue = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

// ─── View Status Modal (Timeline) ─────────────────────────────────────────────

const INITIAL_STEPS = [
  {label: 'Payment Link Sent'},
  {label: 'Client Clicked Link'},
  {label: 'Payment Method Added'},
  {label: 'Proceed with Order'},
]

const TimelineSkeleton = () => (
  <>
    <style>{`
      @keyframes shimmer {
        0% { background-position: -400px 0; }
        100% { background-position: 400px 0; }
      }
      .tl-skeleton {
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 800px 100%;
        animation: shimmer 1.4s infinite linear;
        border-radius: 4px;
      }
    `}</style>
    <div style={{display: 'flex', flexDirection: 'column'}}>
      {INITIAL_STEPS.map((_, i) => (
        <div key={i} style={{display: 'flex', gap: '14px'}}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '20px',
              flexShrink: 0,
            }}
          >
            <div
              className="tl-skeleton"
              style={{width: '20px', height: '20px', borderRadius: '50%'}}
            />
            {i < INITIAL_STEPS.length - 1 && (
              <div
                className="tl-skeleton"
                style={{
                  width: '2px',
                  marginLeft: '9px',
                  flex: 1,
                  minHeight: '20px',
                  borderRadius: '1px',
                }}
              />
            )}
          </div>
          <div
            style={{paddingBottom: i < INITIAL_STEPS.length - 1 ? '20px' : 0}}
          >
            <div
              className="tl-skeleton"
              style={{width: '120px', height: '14px'}}
            />
            <div
              className="tl-skeleton"
              style={{width: '80px', height: '11px', marginTop: '6px'}}
            />
          </div>
        </div>
      ))}
    </div>
  </>
)

const ViewStatusModal = ({link, steps, loading, onClose}) => {
  if (!link) return null

  return (
    <ModalOverlay onClick={onClose}>
      <DetailModalBox
        onClick={e => e.stopPropagation()}
        style={{maxWidth: '500px'}}
      >
        <ModalHeader>
          <div>
            <ModalTitle>Payment Link Timeline</ModalTitle>
            <ModalSubtitle>
              {link.company_name} &mdash; Order #{link.order_id}
            </ModalSubtitle>
          </div>
          <CloseBtn onClick={onClose}>
            <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1L13 13M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </CloseBtn>
        </ModalHeader>

        <ModalScrollContent>
          {loading ? (
            <TimelineSkeleton />
          ) : (
            <div style={{display: 'flex', flexDirection: 'column'}}>
              {steps.map((step, i) => (
                <div key={i} style={{display: 'flex', gap: '14px'}}>
                  {/* dot + connector column */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '20px',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        boxSizing: 'border-box',
                        borderRadius: '50%',
                        flexShrink: 0,
                        backgroundColor: step.completed
                          ? '#fa6000'
                          : theme.colors.border,
                        border: `2px solid ${
                          step.completed ? '#fa6000' : theme.colors.border
                        }`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {step.completed && (
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                          <path
                            d="M1 4L3 6L7 2"
                            stroke="#fff"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    {i < steps.length - 1 && (
                      <div
                        style={{
                          width: '2px',
                          marginLeft: '9px',
                          flex: 1,
                          minHeight: '20px',
                          backgroundColor: theme.colors.border,
                        }}
                      />
                    )}
                  </div>
                  {/* content */}
                  <div
                    style={{paddingBottom: i < steps.length - 1 ? '20px' : 0}}
                  >
                    <span
                      style={{
                        display: 'block',
                        fontWeight: 600,
                        fontSize: '14px',
                        fontFamily: theme.font.family,
                        color: step.completed
                          ? theme.colors.text
                          : theme.colors.placeholder,
                      }}
                    >
                      {step.label}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '12px',
                        fontFamily: theme.font.family,
                        marginTop: '2px',
                        color: step.timestamp
                          ? theme.colors.muted
                          : theme.colors.placeholder,
                      }}
                    >
                      {step.timestamp ? formatDate(step.timestamp) : 'Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ModalScrollContent>

        <ModalActions
          style={{
            padding: '16px 0 24px',
            borderTop: `1px solid ${theme.colors.border}`,
          }}
        >
          <ClearBtn type="button" onClick={onClose}>
            Close
          </ClearBtn>
        </ModalActions>
      </DetailModalBox>
    </ModalOverlay>
  )
}

// ─── Resend Link Modal ────────────────────────────────────────────────────────

const ResendLinkModal = ({link, onClose, onResend, client}) => {
  const [method, setMethod] = useState(null)
  const [sending, setSending] = useState(false)

  if (!link) return null

  const handleSend = async () => {
    if (!method || sending) return
    setSending(true)
    try {
      const is4dot0 = link.flow_version === '4.0'
      const endpoint = is4dot0
        ? 'orders/process/resend-link'
        : 'cc/order/resend-link'
      const response = await client(endpoint, {
        method: 'PATCH',
        data: {
          orderId: String(link.order_id),
          paymentUuid: link.payment_uuid,
          method: method.toLowerCase(),
          ...(method === 'Email'
            ? {email: link.client_email}
            : {contactPhone: link.contact_phone, mobile: link.contact_phone}),
          country: link.business_country,
          appUrl: window.location.origin,
          ...(link.order_header_id
            ? {orderHeaderId: link.order_header_id}
            : {}),
        },
      })
      toast.success('Payment link resent successfully')
      onResend(response?.newUuid || response?.randomId)
      onClose()
    } catch (err) {
      console.error('resend-link error:', err)
      toast.error('Failed to resend link. Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <DetailModalBox
        onClick={e => e.stopPropagation()}
        style={{maxWidth: '420px'}}
      >
        <ModalHeader style={{alignItems: 'center'}}>
          <div>
            <ModalTitle>Resend Payment Link</ModalTitle>
            <ModalSubtitle>
              Choose how to resend the link to{' '}
              <strong>
                {method === 'SMS' ? link.contact_phone : link.client_email}
              </strong>
            </ModalSubtitle>
          </div>
          <CloseBtn onClick={onClose}>
            <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1L13 13M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </CloseBtn>
        </ModalHeader>

        <div style={{display: 'flex', gap: '12px', padding: '4px 0 8px'}}>
          {['Email', 'SMS'].map(opt => (
            <button
              key={opt}
              onClick={() => setMethod(opt)}
              style={{
                flex: 1,
                padding: '14px',
                borderRadius: theme.radius.md,
                border: `2px solid ${
                  method === opt ? '#26325f' : theme.colors.border
                }`,
                backgroundColor: method === opt ? '#eef1f9' : '#ffffff',
                color: method === opt ? '#26325f' : theme.colors.muted,
                fontWeight: 600,
                fontSize: '14px',
                fontFamily: theme.font.family,
                cursor: 'pointer',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {opt}
            </button>
          ))}
        </div>

        <ModalActions
          style={{
            padding: '16px 0 24px',
            borderTop: `1px solid ${theme.colors.border}`,
            display: 'flex',
            gap: '10px',
          }}
        >
          <ClearBtn type="button" onClick={onClose}>
            Cancel
          </ClearBtn>
          <TableBtnOrange
            disabled={!method || sending}
            onClick={handleSend}
            style={{padding: '8px 20px', fontSize: '14px'}}
          >
            {sending ? 'Sending...' : 'Send Link'}
          </TableBtnOrange>
        </ModalActions>
      </DetailModalBox>
    </ModalOverlay>
  )
}

// ─── Order Details Modal ──────────────────────────────────────────────────────

const DetailModalBox = styled(ModalBox)`
  max-width: 580px;
  width: 94%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: 0;
  background: #ffffff;
`

const ModalScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 24px;
  scrollbar-gutter: stable;
  min-height: 220px;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${theme.colors.border};
`

const ModalTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: ${theme.colors.text};
  font-family: ${theme.font.family};
`

const ModalSubtitle = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${theme.colors.muted};
  font-family: ${theme.font.family};
`

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  font-size: 20px;
  cursor: pointer;
  color: ${theme.colors.muted};
  line-height: 1;
  width: 28px;
  height: 28px;
  border-radius: ${theme.radius.sm};
  transition: background ${theme.transition.fast};

  &:hover {
    background: ${theme.colors.hover};
    color: ${theme.colors.text};
  }

  &:focus {
    outline: none;
  }
`

const InfoRow = ({label, value}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: `1px solid ${theme.colors.border}`,
      gap: '16px',
    }}
  >
    <span
      style={{
        fontSize: '13px',
        fontWeight: 600,
        color: theme.colors.text,
        fontFamily: theme.font.family,
        flexShrink: 0,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: '13px',
        color: theme.colors.muted,
        fontFamily: theme.font.family,
        textAlign: 'right',
        wordBreak: 'break-all',
      }}
    >
      {value}
    </span>
  </div>
)

const Section = ({label, children}) => (
  <div style={{marginTop: '20px'}}>
    <p
      style={{
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.colors.muted,
        fontFamily: theme.font.family,
        margin: '0 0 8px',
      }}
    >
      {label}
    </p>
    {children}
  </div>
)

const DetailsModal = ({order, onClose}) => {
  if (!order) return null

  return (
    <ModalOverlay onClick={onClose}>
      <DetailModalBox onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <div>
            <ModalTitle>Order Details</ModalTitle>
            <ModalSubtitle>Order #{order.order_id}</ModalSubtitle>
          </div>
          <CloseBtn onClick={onClose}>
            <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1L13 13M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </CloseBtn>
        </ModalHeader>

        <ModalScrollContent>
          <Section label="Client Info">
            {[
              {
                label: 'Name',
                value: [order.first_name, order.last_name]
                  .filter(Boolean)
                  .join(' '),
              },
              {label: 'Email', value: order.client_email},
              {label: 'Phone', value: order.contact_phone},
              {label: 'Company', value: order.company_name},
              {label: 'Country', value: order.business_country},
              {label: 'Thryv ID', value: order.thryv_id},
            ].map(({label, value}) =>
              value ? (
                <InfoRow key={label} label={label} value={value} />
              ) : null,
            )}
          </Section>

          <Section label="Link Info">
            {[
              {
                label: 'Created',
                value: order.created ? formatDate(order.created) : null,
              },
              {
                label: 'Updated',
                value: order.updated_date
                  ? formatDate(order.updated_date)
                  : null,
              },
              {label: 'Flow', value: order.flow},
              {label: 'Version', value: order.flow_version},
            ].map(({label, value}) =>
              value ? (
                <InfoRow key={label} label={label} value={value} />
              ) : null,
            )}
          </Section>

          <Section label="Created By">
            {[{label: 'User ID', value: order.rep_id}].map(({label, value}) =>
              value ? (
                <InfoRow key={label} label={label} value={value} />
              ) : null,
            )}
          </Section>

          {order.order_notes && (
            <Section label="Order Notes">
              <p
                style={{
                  margin: 0,
                  fontSize: '13px',
                  color: theme.colors.muted,
                  fontFamily: theme.font.family,
                  lineHeight: '1.5',
                }}
              >
                {order.order_notes}
              </p>
            </Section>
          )}
        </ModalScrollContent>

        <ModalActions
          style={{
            padding: '16px 0 24px',
            borderTop: `1px solid ${theme.colors.border}`,
          }}
        >
          <ClearBtn type="button" onClick={onClose}>
            Close
          </ClearBtn>
        </ModalActions>
      </DetailModalBox>
    </ModalOverlay>
  )
}

// ─── Action Cell (reactive expiry) ───────────────────────────────────────────

const ActionCell = ({
  row,
  onViewDetails,
  onViewStatus,
  onResend,
  isOrderComplete,
}) => {
  const [isExpired, setIsExpired] = useState(
    row.expires ? dayjs(row.expires).isBefore(dayjs()) : false,
  )

  useEffect(() => {
    if (!row.expires || isExpired) return
    const timer = setInterval(() => {
      if (dayjs(row.expires).isBefore(dayjs())) {
        setIsExpired(true)
      }
    }, 1000)
    return () => clearInterval(timer)
  }, [row.expires, isExpired])

  // Mirrors PaymentDetailsStep's isOrderComplete check — once the order is
  // confirmed there's nothing left to send/resend a payment link for.
  const isResendDisabled = isExpired || isOrderComplete

  return (
    <div style={{display: 'flex', gap: '8px', whiteSpace: 'nowrap'}}>
      <TableBtn
        onClick={e => {
          e.stopPropagation()
          onViewDetails(row)
        }}
      >
        Details
      </TableBtn>
      <TableBtn
        onClick={e => {
          e.stopPropagation()
          onViewStatus(row)
        }}
      >
        View Status
      </TableBtn>
      {row.expires && (
        <TableBtnOrange
          disabled={isResendDisabled}
          onClick={e => {
            e.stopPropagation()
            if (!isResendDisabled) onResend(row)
          }}
          title={isOrderComplete ? 'Order is already complete' : undefined}
        >
          Resend Link
        </TableBtnOrange>
      )}
    </div>
  )
}

const TableBtn = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: ${theme.radius.full};
  border: 1px solid ${theme.colors.border};
  background: #f4f6fb;
  font-size: 12px;
  font-weight: 500;
  font-family: ${theme.font.family};
  color: ${theme.colors.text};
  cursor: pointer;
  white-space: nowrap;
  transition: background ${theme.transition.fast};

  &:hover:not(:disabled) {
    background: ${theme.colors.hover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const TableBtnOrange = styled(TableBtn)`
  background: #fa6000;
  border-color: #fa6000;
  color: #ffffff;

  &:hover:not(:disabled) {
    background: #e05500;
    color: #ffffff;
  }
`

// ─── Main Screen ──────────────────────────────────────────────────────────────

const COUNT_PER_PAGE = 15

function PendingLinks() {
  const client = useAuthClient()

  const [rows, setRows] = useState([])
  const [perPage, setPerPage] = useState(COUNT_PER_PAGE)
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [viewStatusLink, setViewStatusLink] = useState(null)
  const [viewStatusSteps, setViewStatusSteps] = useState([])
  const [isTimelineLoading, setIsTimelineLoading] = useState(false)
  // payment_uuids learned (via the timeline fetch or a live socket update) to
  // have reached the final "Proceed with Order" step — used to disable that
  // row's Resend Link button, same as PaymentDetailsStep's isOrderComplete.
  const [completedUuids, setCompletedUuids] = useState(() => new Set())
  const [resendLink, setResendLink] = useState(null)
  const [detailsOrder, setDetailsOrder] = useState(null)
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false)
  const dateRangeRef = useRef(null)
  const socketRef = useRef(null)
  const connectedUuidRef = useRef(null)

  useEffect(() => {
    if (!viewStatusLink) return
    const uuid = viewStatusLink.payment_uuid

    const baseSteps = INITIAL_STEPS.map((s, i) => ({
      ...s,
      completed: i === 0,
      timestamp: i === 0 ? viewStatusLink.created : null,
    }))

    const applyPersistedSteps = (steps, persisted) => {
      const updated = steps.map(s => ({...s}))
      for (const {step_number, completed_at} of persisted) {
        // step_number from the server is 1-indexed (1..4); INITIAL_STEPS is
        // 0-indexed (0..3), so mark up to step_number - 1, not step_number.
        for (let i = 0; i < step_number; i++) {
          if (!updated[i]?.completed) {
            updated[i] = {
              ...updated[i],
              completed: true,
              timestamp: completed_at,
            }
          }
        }
      }
      return updated
    }

    // Load persisted steps from the server first
    setIsTimelineLoading(true)
    client(`cc/order/timeline/${uuid}`)
      .then(data => {
        const steps =
          data.success && data.steps?.length
            ? applyPersistedSteps(baseSteps, data.steps)
            : baseSteps
        setViewStatusSteps(steps)
        if (steps[steps.length - 1]?.completed) {
          setCompletedUuids(prev => new Set(prev).add(uuid))
        }
      })
      .catch(() => {
        setViewStatusSteps(baseSteps)
      })
      .finally(() => {
        setIsTimelineLoading(false)
      })

    if (uuid === connectedUuidRef.current) return

    if (socketRef.current) socketRef.current.close()
    connectedUuidRef.current = uuid

    if (!uuid) return

    const socket = io(REACT_APP_OOE_SERVICE_URL, {transports: ['websocket']})
    socketRef.current = socket
    socket.emit('join', {uuid}, () => {})
    socket.on('stepTaken', ({stepNumber}) => {
      setViewStatusSteps(prev => {
        const updated = prev.map(s => ({...s}))
        // stepNumber is 1-indexed (1..4); see applyPersistedSteps above.
        for (let i = 0; i < stepNumber; i++) {
          if (!updated[i]?.completed) {
            updated[i] = {
              ...updated[i],
              completed: true,
              timestamp: new Date().toISOString(),
            }
          }
        }
        if (updated[updated.length - 1]?.completed) {
          setCompletedUuids(prevUuids => new Set(prevUuids).add(uuid))
        }
        return updated
      })
    })
    socket.on('error-message', ({message}) => toast.error(message))
  }, [viewStatusLink, client])

  useEffect(() => {
    return () => {
      if (socketRef.current) socketRef.current.close()
    }
  }, [])

  const [filters, setFilters] = useState({
    companyName: '',
    clientEmail: '',
    thryvId: '',
    startDate: '',
    endDate: '',
  })

  const debouncedCompanyName = useDebouncedValue(filters.companyName)
  const debouncedClientEmail = useDebouncedValue(filters.clientEmail)
  const debouncedThryvId = useDebouncedValue(filters.thryvId)
  const debouncedStartDate = useDebouncedValue(filters.startDate)
  const debouncedEndDate = useDebouncedValue(filters.endDate)

  const updateFilter = (key, value) => {
    setFilters(prev => {
      const updated = {...prev, [key]: value}
      if (key === 'startDate' && prev.endDate && value > prev.endDate) {
        updated.endDate = ''
      }
      return updated
    })
  }

  const clearFilters = () => {
    setFilters({
      companyName: '',
      clientEmail: '',
      thryvId: '',
      startDate: '',
      endDate: '',
    })
    setIsDateRangeOpen(false)
    setPage(1)
  }

  // Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = e => {
      if (
        isDateRangeOpen &&
        dateRangeRef.current &&
        !dateRangeRef.current.contains(e.target)
      ) {
        setIsDateRangeOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDateRangeOpen])

  // Reset to page 1 when any filter changes
  useEffect(() => {
    setPage(1)
  }, [
    debouncedCompanyName,
    debouncedClientEmail,
    debouncedThryvId,
    debouncedStartDate,
    debouncedEndDate,
  ])

  const fetchPendingLinks = useCallback(async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: perPage.toString(),
      })
      if (debouncedCompanyName)
        params.append('companyName', debouncedCompanyName)
      if (debouncedClientEmail)
        params.append('clientEmail', debouncedClientEmail)
      if (debouncedThryvId) params.append('thryvId', debouncedThryvId)
      if (debouncedStartDate) params.append('startDate', debouncedStartDate)
      if (debouncedEndDate) params.append('endDate', debouncedEndDate)

      const data = await client(`cc/order/pending-links?${params}`)

      if (data.success && data.response) {
        setRows(data.response.data || [])
        if (data.response.pagination) {
          setPagination({
            page: data.response.pagination.page,
            totalPages: data.response.pagination.totalPages,
            total: data.response.pagination.total,
            hasNext: data.response.pagination.hasNext,
            hasPrev: data.response.pagination.hasPrev,
          })
        }
      }
    } catch (err) {
      console.error('Error fetching pending links:', err)
      toast.error('Error fetching pending links')
      setRows([])
    } finally {
      setIsLoading(false)
    }
  }, [
    client,
    page,
    debouncedCompanyName,
    debouncedClientEmail,
    debouncedThryvId,
    debouncedStartDate,
    debouncedEndDate,
    perPage,
  ])

  useEffect(() => {
    fetchPendingLinks()
  }, [fetchPendingLinks])

  const handleViewDetails = row => {
    setDetailsOrder(row)
  }

  const handleDateRangeChange = value => {
    if (Array.isArray(value)) {
      const [start, end] = value
      updateFilter('startDate', start ? dayjs(start).format('YYYY-MM-DD') : '')
      updateFilter('endDate', end ? dayjs(end).format('YYYY-MM-DD') : '')
      if (start && end) setIsDateRangeOpen(false)
    }
  }

  const getCalendarValue = () => {
    if (!filters.startDate && !filters.endDate) return null
    const toLocal = str => {
      if (!str) return null
      const [y, m, d] = str.split('-').map(Number)
      return new Date(y, m - 1, d)
    }
    return [toLocal(filters.startDate), toLocal(filters.endDate)]
  }

  const dateRangeLabel = filters.startDate
    ? filters.endDate
      ? `${dayjs(filters.startDate).format('MM/DD/YYYY')} - ${dayjs(
          filters.endDate,
        ).format('MM/DD/YYYY')}`
      : `${dayjs(filters.startDate).format('MM/DD/YYYY')} - Select end date`
    : ''

  // const columns = [
  //   {key: 'order_id', label: 'Order ID'},
  //   {key: 'company_name', label: 'Company Name'},
  //   {
  //     key: 'created',
  //     label: 'Created',
  //     render: date => (
  //       <p style={{margin: 0, fontSize: '12px', whiteSpace: 'nowrap'}}>
  //         {formatDate(date)}
  //       </p>
  //     ),
  //   },
  //   {
  //     key: 'client_email',
  //     label: 'Client Email',
  //     render: email => email || 'N/A',
  //   },
  //   {
  //     key: 'expires',
  //     label: 'Expires',
  //     render: date => (
  //       <p style={{margin: 0, whiteSpace: 'nowrap', fontSize: '12px'}}>
  //         {date ? formatDate(date) : 'N/A'}
  //       </p>
  //     ),
  //   },
  //   {
  //     key: 'expires',
  //     label: 'Time Left',
  //     render: date => <CountdownTimer expiryDate={date} />,
  //   },
  //   {
  //     key: 'actions',
  //     label: 'Action',
  //     render: (_, row) => (
  //       <ActionCell
  //         row={row}
  //         onViewDetails={handleViewDetails}
  //         onViewStatus={setViewStatusLink}
  //         onResend={setResendLink}
  //         isOrderComplete={completedUuids.has(row.payment_uuid)}
  //       />
  //     ),
  //   },
  // ]

  const COLUMNS_DEF = [
    {key: 'order_id', label: 'Order ID'},
    {key: 'company_name', label: 'Company Name'},
    {key: 'created', label: 'Created'},
    {key: 'client_email', label: 'Client Email'},
    {key: 'expires', label: 'Expires'},
    {key: 'time_left', label: 'Time Left'},
    {key: 'actions', label: 'Actions'},
  ]

  return (
    <ContentArea style={{padding: '12px 16px'}}>
      <MainArea style={{fontSize: 'clamp(11px, 1vw, 13px)'}}>
        <FiltersRow>
          <FormField style={{flex: '1 1 180px', minWidth: '160px'}}>
            <FormLabel>Company Name</FormLabel>
            <FormInput
              type="text"
              placeholder="Filter by company"
              value={filters.companyName}
              onChange={e => updateFilter('companyName', e.target.value)}
            />
          </FormField>

          <FormField style={{flex: '1 1 200px', minWidth: '180px'}}>
            <FormLabel>Client Email</FormLabel>
            <FormInput
              type="text"
              placeholder="Filter by email"
              value={filters.clientEmail}
              onChange={e => updateFilter('clientEmail', e.target.value)}
            />
          </FormField>

          <FormField style={{flex: '1 1 160px', minWidth: '140px'}}>
            <FormLabel>Thryv ID</FormLabel>
            <FormInput
              type="text"
              placeholder="Filter by Thryv ID"
              value={filters.thryvId}
              onChange={e => updateFilter('thryvId', e.target.value)}
            />
          </FormField>

          <FormField
            style={{flex: '1 1 260px', minWidth: '220px', position: 'relative'}}
          >
            <FormLabel>Date Range</FormLabel>
            <DateRangeWrapper ref={dateRangeRef} style={{width: '100%'}}>
              <DateRangeTrigger
                type="button"
                hasValue={!!(filters.startDate || filters.endDate)}
                onClick={() => setIsDateRangeOpen(prev => !prev)}
              >
                <img
                  src={PTCalender}
                  width="13"
                  alt=""
                  style={{filter: 'brightness(0) invert(0.5)', flexShrink: 0}}
                />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {dateRangeLabel || 'MM/DD/YYYY - MM/DD/YYYY'}
                </span>
                {filters.startDate || filters.endDate ? (
                  <span
                    role="button"
                    onClick={e => {
                      e.stopPropagation()
                      updateFilter('startDate', '')
                      updateFilter('endDate', '')
                      setIsDateRangeOpen(false)
                    }}
                    style={{fontSize: '16px', lineHeight: 1, cursor: 'pointer'}}
                  >
                    ×
                  </span>
                ) : (
                  <img
                    src={PTCaretDown}
                    width="10"
                    alt=""
                    style={{
                      filter: 'brightness(0) invert(0.5)',
                      flexShrink: 0,
                      transform: isDateRangeOpen
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                      transition: 'transform 0.15s ease',
                    }}
                  />
                )}
              </DateRangeTrigger>
              {isDateRangeOpen && (
                <CalendarPopout style={{left: 0, right: 'auto'}}>
                  <Calendar
                    selectRange
                    onChange={handleDateRangeChange}
                    value={getCalendarValue()}
                  />
                </CalendarPopout>
              )}
            </DateRangeWrapper>
          </FormField>

          <div style={{display: 'flex', alignItems: 'flex-end', gap: '8px'}}>
            <ClearBtn type="button" onClick={clearFilters}>
              Clear Filters
            </ClearBtn>
            <ClearBtn
              type="button"
              onClick={fetchPendingLinks}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: isLoading ? 0.6 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              <RefreshIcon
                width="14"
                height="14"
                style={{
                  animation: isLoading ? 'spin 0.8s linear infinite' : 'none',
                }}
              />
              Refresh
            </ClearBtn>
          </div>
        </FiltersRow>

        <TableWrapper>
          <StyledTable>
            <THead>
              <TR>
                {COLUMNS_DEF.map(col => (
                  <TH key={col.key}>{col.label}</TH>
                ))}
              </TR>
            </THead>
            <TBody>
              {isLoading ? (
                Array.from({length: perPage}).map((_, i) => (
                  <TR key={`sk-${i}`}>
                    {COLUMNS_DEF.map(col => (
                      <TD key={col.key}>
                        <div
                          style={{
                            height: '14px',
                            borderRadius: '4px',
                            background:
                              'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                            backgroundSize: '200% 100%',
                            animation: 'skeleton-shimmer 1.5s infinite',
                            width: col.key === 'actions' ? '100px' : '80%',
                          }}
                        />
                      </TD>
                    ))}
                  </TR>
                ))
              ) : rows.length === 0 ? (
                <TR>
                  <TD colSpan={COLUMNS_DEF.length}>
                    <EmptyState>
                      <EmptyStateTitle>
                        No Pending Purchase Links found.
                      </EmptyStateTitle>
                    </EmptyState>
                  </TD>
                </TR>
              ) : (
                rows.map((row, idx) => (
                  <TR key={row.order_id || idx}>
                    <TD>{row.order_id}</TD>
                    <TD>{row.company_name}</TD>
                    <TD>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatDate(row.created)}
                      </p>
                    </TD>
                    <TD>{row.client_email || 'N/A'}</TD>
                    <TD>
                      <p
                        style={{
                          margin: 0,
                          whiteSpace: 'nowrap',
                          fontSize: '12px',
                        }}
                      >
                        {row.expires ? formatDate(row.expires) : 'N/A'}
                      </p>
                    </TD>
                    <TD>
                      <CountdownTimer expiryDate={row.expires} />
                    </TD>
                    <TD>
                      <ActionCell
                        row={row}
                        onViewDetails={handleViewDetails}
                        onViewStatus={setViewStatusLink}
                        onResend={setResendLink}
                        isOrderComplete={completedUuids.has(row.payment_uuid)}
                      />
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </StyledTable>
        </TableWrapper>

        {pagination && pagination.total > 0 && (
          <ProposalPagination
            currentPage={pagination.page}
            totalItems={pagination.total}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={value => {
              setPerPage(value)
              setPage(1)
            }}
          />
        )}
      </MainArea>

      {/* Modals */}
      <DetailsModal
        order={detailsOrder}
        onClose={() => setDetailsOrder(null)}
      />
      <ViewStatusModal
        link={viewStatusLink}
        steps={viewStatusSteps}
        loading={isTimelineLoading}
        onClose={() => setViewStatusLink(null)}
      />
      <ResendLinkModal
        link={resendLink}
        onClose={() => setResendLink(null)}
        onResend={newUuid => {
          if (socketRef.current) socketRef.current.close()
          socketRef.current = null
          connectedUuidRef.current = null
          if (newUuid) {
            setRows(prev =>
              prev.map(r =>
                r.payment_uuid === resendLink?.payment_uuid
                  ? {...r, payment_uuid: newUuid}
                  : r,
              ),
            )
          }
          setViewStatusSteps(
            INITIAL_STEPS.map((s, i) => ({
              ...s,
              completed: i === 0,
              timestamp: i === 0 ? resendLink?.created : null,
            })),
          )
        }}
        client={client}
      />

      <style>{`
        @keyframes skeleton-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </ContentArea>
  )
}

export {PendingLinks}
